import React, { Suspense, ReactNode } from 'react';
import { CenteredLoadingIndicator } from '@spotify-internal/anchor-ds';
import { KeyboardDetectionProvider } from '@spotify-internal/encore-web';
import { HelmetProvider } from 'react-helmet-async';
import { I18NClientProvider } from 'client/i18n/context';
import { BrowserSizeProvider } from './BrowserSize';
import { NetworkRoleProvider } from './NetworkRole';
import { UnsavedChangesWarningProvider } from './UnsavedChangesWarning';
import { FeatureFlagsProvider } from './FeatureFlags';
import { CurrentUserProvider } from './CurrentUser';
import { ExperimentationProvider } from './Experimentation';
import { ReactQueryProvider } from './ReactQuery';
import { QuickUploadFileProvider } from './QuickUploadFile';

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <HelmetProvider>
      <ReactQueryProvider>
        <CurrentUserProvider>
          <NetworkRoleProvider>
            <ExperimentationProvider>
              <FeatureFlagsProvider>
                <QuickUploadFileProvider>
                  <BrowserSizeProvider>
                    <KeyboardDetectionProvider>
                      <I18NClientProvider>
                        {/* 
                          This React Suspense block is required for handling the translation resources loading process.
                          The Suspense block will render the fallback UI when user changes language and while the translations for that language are being loaded.
                          Read more about Suspense here: https://reactjs.org/docs/concurrent-mode-suspense.html
                          
                          IMPORTANT: If you're adding or editing any context in this file that uses the translation function (t), 
                          you have to put the context within the Suspense block.
                        */}
                        <Suspense
                          fallback={
                            <div>
                              <CenteredLoadingIndicator size="lg" />
                            </div>
                          }
                        >
                          <UnsavedChangesWarningProvider>
                            {children}
                          </UnsavedChangesWarningProvider>
                        </Suspense>
                      </I18NClientProvider>
                    </KeyboardDetectionProvider>
                  </BrowserSizeProvider>
                </QuickUploadFileProvider>
              </FeatureFlagsProvider>
            </ExperimentationProvider>
          </NetworkRoleProvider>
        </CurrentUserProvider>
      </ReactQueryProvider>
    </HelmetProvider>
  );
}
